/**
 * The administration screens (M8-P2 §6.2 – §6.9).
 *
 * Deliberately plain DOM plus the measured classes in `admin.css`: every table here has a
 * captured geometry (`discovery/ui-reference/admin/p1p2-*.json`) and a wrapper component that
 * quietly adds a pixel of padding is exactly what round 5's verification passes kept finding.
 * The only shared components used are the ones whose box we control.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
/**
 * P-009 (round 7b): the row menu and the role listbox used to lay out **in flow** inside
 * their `<td>`, which widened the table — the menu's right edge measured 1,616 in a 1,440
 * window and it was simply off-screen on a phone. They go through the one overlay primitive
 * now (body portal, clamped into the viewport, flips, Escape closes and focus returns).
 */
import {
  AnchoredPopover,
  formatMondayDate,
  formatMondayDateTime,
  useConfirm,
  useOptionalToast,
  useOverlayDismiss,
} from '@elizmonday/ui';
/**
 * M8-P3 (T-9u) — "View as <user>". The dialog, the Security list and the browser reset live in
 * their own module with their own stylesheet: nothing about this feature is monday's, and
 * mixing it into `admin.css` would make its numbers look measured.
 */
import {
  ActiveImpersonationsView,
  ViewAsDialogView,
  leaveTheView,
  type ActiveImpersonationRow,
} from './impersonation';
import type {
  AccountRole,
  AdminSource,
  AuditQuery,
  AuditRow,
  CapabilityCell,
  DirectoryPage,
  DirectoryQuery,
  DirectoryUserRow,
  InvitationRow,
  PeopleSearchRow,
  PublicAccount,
  SecuritySettings,
  TeamDetail,
  TeamSummary,
  TokenOverviewRow,
  UsageStats,
  UserDetail,
  UserStatus,
} from './types';

/** monday's four — the captured role listbox, the facet chips and the capability matrix. */
const ROLES: AccountRole[] = ['admin', 'member', 'viewer', 'guest'];
const STATUSES: UserStatus[] = ['active', 'inactive', 'pending'];

const TITLE_CASE: Record<string, string> = {
  /** Ours, not monday's (T-9f): shown wherever a role is rendered, offered only to a superadmin. */
  superadmin: 'Super admin',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
  guest: 'Guest',
  active: 'Active',
  inactive: 'Inactive',
  pending: 'Pending',
};

/** An admin column the server withheld (P-250) reads the same as an empty one. */
const shortDate = (iso: string | null | undefined): string =>
  iso === null || iso === undefined
    ? ''
    : formatMondayDate(iso, { day: '2-digit' });

/**
 * **P-242 — the console answers a write the way every other surface does.**
 *
 * The admin screens write through `AdminSource` directly rather than through TanStack Query's
 * `MutationCache`, which is where `app/errors.ts` hangs the product's toasts; so Users printed
 * an inline `role="status"` line of its own and General, Security, Permissions and Teams said
 * nothing at all. This is the `onSuccess`/`onError` hook the card asks for: one helper, wired
 * to the same `useToast` every other module uses.
 *
 * `useOptionalToast`, not `useToast`: `/__ui/admin` and the jsdom suite mount these screens
 * with no `UiProvider`, and a screen must not throw because nobody is there to be told.
 *
 * **P-248 — and an Undo when there is something to undo.** `undo` is the *previous value*,
 * replayed through the same write, which is why every caller that has one passes a closure
 * rather than a flag.
 */
function useAdminWrite(): (
  run: () => Promise<unknown>,
  options: { success: string; undo?: (() => Promise<unknown>) | undefined; after?: () => void },
) => Promise<boolean> {
  const toast = useOptionalToast();
  return useCallback(
    async (run, { success, undo, after }) => {
      try {
        await run();
        toast(success, {
          kind: 'positive',
          ...(undo
            ? {
                action: {
                  label: 'Undo',
                  onClick: () => {
                    void undo()
                      .then(() => after?.())
                      .catch(() => undefined);
                  },
                },
              }
            : {}),
        });
        after?.();
        return true;
      } catch (cause: unknown) {
        toast(cause instanceof Error ? cause.message : String(cause), { kind: 'negative' });
        after?.();
        return false;
      }
    },
    [toast],
  );
}

/**
 * P-248 — the **previous value** of everything a General/Customization patch touches, shaped as
 * the patch that puts it back. `account_settings` columns and `PublicAccount` fields are not
 * named the same (`wordmarkText` vs `wordmark`, `brandPrimary` vs `brand.primary`), so the map
 * is written out rather than guessed; a key that is not in it is simply not undoable.
 */
function inverseAccountPatch(
  before: PublicAccount,
  patch: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const inverse: Record<string, unknown> = {};
  for (const key of Object.keys(patch)) {
    switch (key) {
      case 'name':
        inverse[key] = before.name;
        break;
      case 'productName':
        inverse[key] = before.productName;
        break;
      case 'wordmarkText':
        inverse[key] = before.wordmark;
        break;
      case 'logoUrl':
        inverse[key] = before.logoUrl;
        break;
      case 'emailHeaderUrl':
        inverse[key] = before.emailHeaderUrl;
        break;
      case 'brandPrimary':
        inverse[key] = before.brand.primary;
        break;
      case 'brandPrimaryHover':
        inverse[key] = before.brand.hover;
        break;
      case 'brandTextOnPrimary':
        inverse[key] = before.brand.onPrimary;
        break;
      case 'weekendDays':
        inverse[key] = before.weekendDays;
        break;
      case 'showWeekends':
        inverse[key] = before.showWeekends;
        break;
      case 'firstDayOfTheWeek':
        inverse[key] = before.firstDayOfTheWeek;
        break;
      default:
        return undefined;
    }
  }
  return Object.keys(inverse).length > 0 ? inverse : undefined;
}

/** What the toast calls the thing that was just saved. */
const ACCOUNT_FIELD_NAMES: Record<string, string> = {
  name: 'Account name',
  productName: 'Product name',
  wordmarkText: 'Wordmark',
  logoUrl: 'Logo',
  emailHeaderUrl: 'E-mail header',
  brandPrimary: 'Brand colour',
  brandPrimaryHover: 'Brand colour',
  brandTextOnPrimary: 'Brand text colour',
  weekendDays: 'Weekend days',
  showWeekends: 'Weekends on calendars',
  firstDayOfTheWeek: 'First day of the week',
};

function describeAccountPatch(patch: Record<string, unknown>): string {
  const names = [...new Set(Object.keys(patch).map((key) => ACCOUNT_FIELD_NAMES[key] ?? key))];
  return names.length === 1 ? names[0]! : 'Account settings';
}

/**
 * P-248 — the previous value of the Security fields, shaped as the patch that restores them.
 * The SSO switches are one jsonb column the service merges partially, so their inverse is a
 * partial object too. A key this map does not know is simply not undoable.
 */
function inverseSecurityPatch(
  before: SecuritySettings | undefined,
  patch: Record<string, unknown>,
): Record<string, unknown> | undefined {
  if (!before) return undefined;
  const inverse: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch)) {
    if (key === 'sso') {
      const sso: Record<string, unknown> = {};
      for (const switchKey of Object.keys(value as Record<string, unknown>)) {
        if (switchKey === 'enabled') sso[switchKey] = before.sso.enabled;
        else if (switchKey === 'allowPasswordLogin') sso[switchKey] = before.sso.allowPasswordLogin;
        else if (switchKey === 'autoProvision') sso[switchKey] = before.sso.autoProvision;
        else return undefined;
      }
      inverse[key] = sso;
      continue;
    }
    if (!(key in before)) return undefined;
    inverse[key] = (before as unknown as Record<string, unknown>)[key];
  }
  return Object.keys(inverse).length > 0 ? inverse : undefined;
}

/** A tiny async hook: this module never pulls in a query library of its own. */
function useAsync<T>(load: () => Promise<T>, deps: unknown[]): {
  data: T | undefined;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let live = true;
    load()
      .then((value) => {
        if (live) {
          setData(value);
          setError(null);
        }
      })
      .catch((cause: unknown) => {
        if (live) setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      live = false;
    };
  }, [...deps, nonce]);
  return { data, error, reload: () => setNonce((n) => n + 1) };
}

/* ══════════════════════════════════════════════ Directory → Users (§6.2) */

export function UsersScreen({ source }: { source: AdminSource }): React.ReactElement {
  const [query, setQuery] = useState<DirectoryQuery>({ limit: 50 });
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [roleFor, setRoleFor] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** M8-P3 (T-9u): the row a *"View as <name>"* was asked for, and how it went. */
  const [viewAs, setViewAs] = useState<DirectoryUserRow | null>(null);
  /** The person whose profile page is open (M8-P2 §6.2), the second "View as" entry point. */
  const [profileFor, setProfileFor] = useState<string | null>(null);
  const [viewAsPending, setViewAsPending] = useState(false);
  const [viewAsError, setViewAsError] = useState<string | null>(null);

  const effective = useMemo<DirectoryQuery>(
    () => ({ ...query, ...(search ? { search } : {}) }),
    [query, search],
  );
  const { data, error, reload } = useAsync<DirectoryPage>(
    () => source.directory(effective),
    [JSON.stringify(effective)],
  );

  const toggleFacet = useCallback((key: 'roles' | 'statuses' | 'teamIds', value: string) => {
    setQuery((current) => {
      const list = (current[key] ?? []) as string[];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      return { ...current, [key]: next };
    });
  }, []);

  /**
   * P-242: the inline `role="status"` line is monday's own and stays; the product's toast now
   * joins it, so a role change announces itself here exactly as it does everywhere else.
   */
  const toast = useOptionalToast();
  const run = useCallback(
    async (label: string, action: () => Promise<{ updated: string[]; errors: Array<{ message: string }> }>) => {
      try {
        const result = await action();
        const message =
          result.errors.length === 0
            ? `${label}: ${result.updated.length} updated.`
            : `${label}: ${result.updated.length} updated, ${result.errors.length} failed — ${result.errors[0]?.message ?? ''}`;
        setNotice(message);
        toast(message, { kind: result.errors.length === 0 ? 'positive' : 'warning' });
      } catch (cause: unknown) {
        const message = cause instanceof Error ? cause.message : String(cause);
        setNotice(message);
        toast(message, { kind: 'negative' });
      }
      setSelected(new Set());
      setMenuFor(null);
      setRoleFor(null);
      reload();
    },
    [reload, toast],
  );

  const rows = data?.rows ?? [];

  return (
    <div data-testid="admin-users">
      <h1 className="ez-admin__heading">User management</h1>
      <div className="ez-admin__toolbar">
        <input
          className="ez-admin__search"
          type="search"
          placeholder="Search"
          aria-label="Search users"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ height: 32, width: 240, padding: '0 10px', borderRadius: 4 }}
        />
        <button type="button" onClick={() => setFilterOpen((open) => !open)} data-testid="admin-users-filter">
          Filter
        </button>
        <span className="ez-admin__count" data-testid="admin-users-count">
          Showing: {data?.total ?? 0} results
        </span>
      </div>

      {filterOpen ? (
        <div className="ez-admin-filter" role="dialog" aria-label="Filter users" data-testid="admin-users-filter-panel">
          <div>
            <div className="ez-admin-filter__title">User role</div>
            {ROLES.map((role) => (
              <button
                key={role}
                type="button"
                className="ez-admin-filter__chip"
                aria-pressed={(query.roles ?? []).includes(role)}
                onClick={() => toggleFacet('roles', role)}
              >
                {TITLE_CASE[role]}
                <span style={{ marginLeft: 'auto', opacity: 0.6 }}>{data?.facets.roles[role] ?? 0}</span>
              </button>
            ))}
          </div>
          <div>
            <div className="ez-admin-filter__title">Status</div>
            {STATUSES.map((status) => (
              <button
                key={status}
                type="button"
                className="ez-admin-filter__chip"
                aria-pressed={(query.statuses ?? []).includes(status)}
                onClick={() => toggleFacet('statuses', status)}
              >
                {TITLE_CASE[status]}
                <span style={{ marginLeft: 'auto', opacity: 0.6 }}>{data?.facets.statuses[status] ?? 0}</span>
              </button>
            ))}
          </div>
          <div>
            <div className="ez-admin-filter__title">Products</div>
            <button type="button" className="ez-admin-filter__chip" aria-pressed disabled>
              monday
            </button>
          </div>
          <div>
            <div className="ez-admin-filter__title">Teams</div>
            <button
              type="button"
              className="ez-admin-filter__chip"
              aria-pressed={query.noTeam === true}
              onClick={() => setQuery((current) => ({ ...current, noTeam: !current.noTeam }))}
            >
              No teams
              <span style={{ marginLeft: 'auto', opacity: 0.6 }}>{data?.facets.noTeam ?? 0}</span>
            </button>
            {(data?.facets.teams ?? []).map((team) => (
              <button
                key={team.id}
                type="button"
                className="ez-admin-filter__chip"
                aria-pressed={(query.teamIds ?? []).includes(team.id)}
                onClick={() => toggleFacet('teamIds', team.id)}
              >
                {team.name}
                <span style={{ marginLeft: 'auto', opacity: 0.6 }}>{team.count}</span>
              </button>
            ))}
          </div>
          <div className="ez-admin-filter__footer">
            <button type="button" onClick={() => setQuery({ limit: 50 })}>
              Clear all
            </button>
            <span className="ez-admin__count">
              Facets combine as OR inside a column and AND across columns.
            </span>
          </div>
        </div>
      ) : null}

      {error ? <p role="alert">{error}</p> : null}
      {notice ? (
        <p role="status" data-testid="admin-users-notice">
          {notice}
        </p>
      ) : null}

      <div className="ez-admin-table__scroll">
        <table className="ez-admin-table ez-admin-users" data-testid="admin-users-table">
          <colgroup>
            <col style={{ width: 40 }} />
            <col className="c-name" />
            <col className="c-email" />
            <col className="c-role" />
            <col className="c-product" />
            <col className="c-status" />
            <col className="c-teams" />
            <col className="c-joined" />
            <col className="c-invited" />
            <col className="c-active" />
            <col className="c-2fa" />
            <col className="c-platform" />
            <col style={{ width: 48 }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  aria-label="Select all users"
                  checked={rows.length > 0 && selected.size === rows.length}
                  onChange={(event) =>
                    setSelected(event.target.checked ? new Set(rows.map((row) => row.id)) : new Set())
                  }
                />
              </th>
              <th>Name</th>
              <th>Email</th>
              <th>User role</th>
              <th>Products</th>
              <th>Status</th>
              <th>Teams</th>
              <th>Joined</th>
              <th>Invited by</th>
              <th>Last active</th>
              <th>2FA</th>
              <th>Platform</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <UserRow
                key={row.id}
                row={row}
                canGrantSuperAdmin={data?.canGrantSuperAdmin === true}
                selected={selected.has(row.id)}
                onSelect={(on) =>
                  setSelected((current) => {
                    const next = new Set(current);
                    if (on) next.add(row.id);
                    else next.delete(row.id);
                    return next;
                  })
                }
                roleOpen={roleFor === row.id}
                onRoleOpen={() => setRoleFor(roleFor === row.id ? null : row.id)}
                onRoleClose={() => setRoleFor(null)}
                onRole={(role) => run('Role changed', () => source.setRole({ userIds: [row.id], role }))}
                menuOpen={menuFor === row.id}
                onMenuOpen={() => setMenuFor(menuFor === row.id ? null : row.id)}
                onMenuClose={() => setMenuFor(null)}
                canViewAsUser={data?.canViewAsUser === true}
                onOpenProfile={() => setProfileFor(row.id)}
                onViewAs={() => {
                  setMenuFor(null);
                  setViewAsError(null);
                  setViewAs(row);
                }}
                onDeactivate={() => run('Deactivated', () => source.deactivate([row.id]))}
                onReactivate={() =>
                  run('Reactivated', () => source.reactivate({ userIds: [row.id], notify: true }))
                }
                onResetPassword={() =>
                  run('Password reset sent', async () => {
                    await source.resetPassword(row.id);
                    return { updated: [row.id], errors: [] };
                  })
                }
                onUnlinkPlatform={() =>
                  run('Unlinked from Eli Z Platform', async () => {
                    await source.unlinkPlatform(row.id);
                    return { updated: [row.id], errors: [] };
                  })
                }
              />
            ))}
          </tbody>
        </table>
      </div>

      {selected.size > 0 ? (
        <div data-testid="admin-users-bulk" style={{ marginTop: 12, display: 'flex', gap: 8 }}>
          <span className="ez-admin__count">{selected.size} selected</span>
          <button
            type="button"
            onClick={() => run('Deactivated', () => source.deactivate([...selected]))}
          >
            Deactivate
          </button>
          <button
            type="button"
            onClick={() =>
              run('Reactivated', () => source.reactivate({ userIds: [...selected], notify: false }))
            }
          >
            Reactivate
          </button>
          <button
            type="button"
            onClick={() => run('Role changed', () => source.setRole({ userIds: [...selected], role: 'member' }))}
          >
            Make members
          </button>
        </div>
      ) : null}

      {profileFor ? (
        <div className="ez-dialog-backdrop" data-testid="admin-user-profile-backdrop">
          <UserProfilePanel
            source={source}
            userId={profileFor}
            canViewAsUser={data?.canViewAsUser === true}
            onClose={() => setProfileFor(null)}
            onViewAs={(user) => {
              setProfileFor(null);
              setViewAsError(null);
              setViewAs(user);
            }}
          />
        </div>
      ) : null}

      {/*
       * M8-P3 §2 — the confirmation. It is not a `useConfirm` one-liner on purpose: the
       * dialog has to *state* what a view is (read-only, silent, audited, two hours) before
       * somebody opens another person's product, and that is four sentences, not a title.
       */}
      {viewAs ? (
        <div className="ez-dialog-backdrop" data-testid="view-as-backdrop">
          <ViewAsDialogView
            user={viewAs}
            pending={viewAsPending}
            error={viewAsError}
            onCancel={() => {
              if (!viewAsPending) setViewAs(null);
            }}
            onConfirm={() => {
              if (viewAsPending) return;
              setViewAsPending(true);
              setViewAsError(null);
              void (async () => {
                try {
                  await source.startImpersonation(viewAs.id);
                  /*
                   * The cookie is the target's now; a document load is the reset. No query
                   * client is needed — and asking for one here would make this screen
                   * unrenderable outside a `QueryClientProvider`, which is how `/__ui/admin`
                   * and this module's own unit tests render it.
                   */
                  await leaveTheView(null, '/');
                } catch (cause: unknown) {
                  setViewAsPending(false);
                  setViewAsError(cause instanceof Error ? cause.message : String(cause));
                }
              })();
            }}
          />
        </div>
      ) : null}
    </div>
  );
}

function UserRow(props: {
  row: DirectoryUserRow;
  /** T-9f: the fifth role is offered only when the server says this caller may grant it. */
  canGrantSuperAdmin: boolean;
  selected: boolean;
  onSelect: (on: boolean) => void;
  roleOpen: boolean;
  onRoleOpen: () => void;
  onRoleClose: () => void;
  onRole: (role: AccountRole) => void;
  menuOpen: boolean;
  onMenuOpen: () => void;
  onMenuClose: () => void;
  /** M8-P3 (T-9u): the server says whether this caller may open a view at all. */
  canViewAsUser: boolean;
  onViewAs: () => void;
  /** The name cell opens the person's profile page (M8-P2 §6.2). */
  onOpenProfile: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  onResetPassword: () => void;
  onUnlinkPlatform: () => void;
}): React.ReactElement {
  const { row } = props;
  const confirm = useConfirm();
  /** The anchor is state, not a ref: the popover has to re-place when the button mounts. */
  const [roleAnchor, setRoleAnchor] = useState<HTMLButtonElement | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  return (
    <tr data-testid={`admin-user-row-${row.id}`}>
      <td>
        <input
          type="checkbox"
          aria-label={`Select ${row.name}`}
          checked={props.selected}
          onChange={(event) => props.onSelect(event.target.checked)}
        />
      </td>
      <td title={row.name}>
        {/*
         * The name opens that person's profile page. It was plain text until T-9u; the console
         * had no per-person screen at all, which is where "View as" wanted its second entry
         * point and where an admin expects a person's sessions and tokens to be.
         */}
        <button
          type="button"
          className="ez-admin-linkish"
          data-testid={`admin-user-name-${row.id}`}
          onClick={props.onOpenProfile}
          style={{ border: 0, background: 'transparent', font: 'inherit', padding: 0, cursor: 'pointer' }}
        >
          {row.name}
        </button>
        {row.isDeleted ? <span className="ez-admin-chip ez-admin-chip--deleted">deleted</span> : null}
        {row.isNonHuman ? <span className="ez-admin-chip">non-human</span> : null}
      </td>
      <td title={row.email}>{row.email}</td>
      <td style={{ position: 'relative' }}>
        <button
          ref={setRoleAnchor}
          type="button"
          onClick={props.onRoleOpen}
          data-testid={`admin-user-role-${row.id}`}
          style={{ border: 0, background: 'transparent', font: 'inherit', cursor: 'pointer' }}
        >
          {TITLE_CASE[row.role]}
        </button>
        <AnchoredPopover
          anchor={roleAnchor}
          open={props.roleOpen}
          onClose={props.onRoleClose}
          label={`Account role for ${row.name}`}
          role="listbox"
          align="start"
          data-testid="admin-role-dropdown"
        >
          <div className="ez-admin-roles">
            {/*
              monday's four, and — for a superadmin only — ours. The listbox of an ordinary
              admin stays exactly the captured one (P2 acceptance A4); a superadmin's row for
              another superadmin still shows "Super admin" through `TITLE_CASE`.
            */}
            {[...ROLES, ...(props.canGrantSuperAdmin ? (['superadmin'] as AccountRole[]) : [])].map(
              (role) => (
                <button
                  key={role}
                  type="button"
                  role="option"
                  aria-selected={role === row.role}
                  className="ez-admin-roles__option"
                  onClick={() => props.onRole(role)}
                >
                  {TITLE_CASE[role]}
                </button>
              ),
            )}
          </div>
        </AnchoredPopover>
      </td>
      <td>{row.product}</td>
      <td
        title={
          row.deactivatedAt
            ? `Deactivated by ${row.deactivatedBy?.name ?? 'an admin'} on ${shortDate(row.deactivatedAt)}`
            : undefined
        }
      >
        {TITLE_CASE[row.status]}
      </td>
      <td>{row.teams.map((team) => team.name).join(', ')}</td>
      <td>{shortDate(row.joinedAt)}</td>
      <td>{row.invitedBy?.name ?? ''}</td>
      <td>{shortDate(row.lastActivityAt)}</td>
      <td>Disabled</td>
      {/* M0-F3b §8.3: `subject` is the platform username, the stable key SSO is built on. */}
      <td
        data-testid={`admin-user-platform-${row.id}`}
        title={
          row.platform
            ? `Linked ${shortDate(row.platform.linkedAt)}${
                row.platform.lastLoginAt ? `, last sign-in ${shortDate(row.platform.lastLoginAt)}` : ''
              }`
            : undefined
        }
      >
        {row.platform ? `Linked as ${row.platform.subject}` : 'Not linked'}
      </td>
      <td style={{ position: 'relative' }}>
        <button
          ref={setMenuAnchor}
          type="button"
          aria-label={`Actions for ${row.name}`}
          onClick={props.onMenuOpen}
          data-testid={`admin-user-menu-${row.id}`}
          style={{ border: 0, background: 'transparent', cursor: 'pointer', width: 24 }}
        >
          ⋯
        </button>
        <AnchoredPopover
          anchor={menuAnchor}
          open={props.menuOpen}
          onClose={props.onMenuClose}
          label={`Actions for ${row.name}`}
          role="menu"
          align="end"
          data-testid="admin-row-menu"
        >
          <div className="ez-admin-rowmenu">
            {/*
             * M8-P3 §2 — **ours, not monday's**. First in the menu because it is the reason a
             * superadmin opened it, and absent (never disabled, F8 §8) for anyone who may not
             * use it: a non-superadmin caller, another superadmin's row, and a tombstone.
             */}
            {props.canViewAsUser && row.role !== 'superadmin' && !row.isDeleted ? (
              <button
                type="button"
                role="menuitem"
                className="ez-admin-rowmenu__item"
                data-testid={`admin-user-view-as-${row.id}`}
                onClick={props.onViewAs}
              >
                View as {row.name}
              </button>
            ) : null}
            <button type="button" role="menuitem" className="ez-admin-rowmenu__item" disabled>
              Edit email address
            </button>
            <button
              type="button"
              role="menuitem"
              className="ez-admin-rowmenu__item"
              onClick={props.onResetPassword}
              disabled={row.isDeleted}
            >
              Reset password
            </button>
            {row.platform ? (
              <button
                type="button"
                role="menuitem"
                className="ez-admin-rowmenu__item"
                data-testid={`admin-user-unlink-${row.id}`}
                onClick={() => {
                  void (async () => {
                    const ok = await confirm({
                      title: `Unlink ${row.name} from Eli Z Platform?`,
                      body: `They will stop signing in as "${row.platform?.subject}". Their account, role and boards are not changed, and signing in with the platform again re-links them.`,
                      confirmLabel: 'Unlink',
                      danger: true,
                    });
                    if (ok) props.onUnlinkPlatform();
                  })();
                }}
              >
                Unlink from Eli Z Platform
              </button>
            ) : null}
            {row.status === 'inactive' ? (
              <button
                type="button"
                role="menuitem"
                className="ez-admin-rowmenu__item"
                onClick={props.onReactivate}
              >
                Reactivate user
              </button>
            ) : (
              <button
                type="button"
                role="menuitem"
                className="ez-admin-rowmenu__item"
                onClick={props.onDeactivate}
              >
                Deactivate user
              </button>
            )}
          </div>
        </AnchoredPopover>
      </td>
    </tr>
  );
}

/**
 * One person's profile page inside the console (M8-P2 §6.2 · M8-P3 §2).
 *
 * Deliberately small: identity, role, status, teams, and the three counts that only an
 * administrator can see (open sessions, live API tokens, boards owned). It is **the second
 * entry point** for *"View as <name>"* — the first is the row menu — because that is where a
 * superadmin ends up when they are already looking at one person and start wondering what
 * that person sees.
 *
 * Every geometry here is ours: monday has no per-person page in its admin console
 * (`discovery/ui-reference/admin/INDEX.md`), so the panel borrows `admin.css`'s existing card
 * classes rather than inventing measurements that would look captured.
 */
function UserProfilePanel({
  source,
  userId,
  canViewAsUser,
  onClose,
  onViewAs,
}: {
  source: AdminSource;
  userId: string;
  canViewAsUser: boolean;
  onClose: () => void;
  onViewAs: (user: DirectoryUserRow) => void;
}): React.ReactElement {
  const { data, error } = useAsync<UserDetail>(() => source.user(userId), [userId]);
  /** P-260: Escape closes the panel and focus returns to the directory row that opened it. */
  useOverlayDismiss({ onClose });

  return (
    <div
      className="ez-admin-card"
      role="dialog"
      aria-modal="true"
      aria-label={data ? `Profile of ${data.name}` : 'Profile'}
      data-testid="admin-user-profile"
      style={{ width: 520, maxWidth: 'calc(100vw - 32px)', maxHeight: '80vh', overflow: 'auto' }}
    >
      {error ? <p role="alert">{error}</p> : null}
      {!data ? (
        <p className="ez-admin__hint">Loading…</p>
      ) : (
        <>
          <h3 data-testid="admin-user-profile-name">{data.name}</h3>
          <p className="ez-admin__hint">{data.email}</p>

          <div className="ez-admin-field">
            <span>User role</span>
            <span>{TITLE_CASE[data.role] ?? data.role}</span>
          </div>
          <div className="ez-admin-field">
            <span>Status</span>
            <span>{TITLE_CASE[data.status] ?? data.status}</span>
          </div>
          <div className="ez-admin-field">
            <span>Teams</span>
            <span>{data.teams.map((team) => team.name).join(', ') || '—'}</span>
          </div>
          <div className="ez-admin-field">
            <span>Joined</span>
            <span>{shortDate(data.joinedAt)}</span>
          </div>
          <div className="ez-admin-field">
            <span>Last active</span>
            <span>{shortDate(data.lastActivityAt)}</span>
          </div>
          <div className="ez-admin-field">
            <span>Open sessions · API tokens · boards owned</span>
            <span data-testid="admin-user-profile-counts">
              {data.sessionCount} · {data.tokenCount} · {data.boardsOwned}
            </span>
          </div>
          <div className="ez-admin-field">
            <span>Platform</span>
            <span>{data.platform ? `Linked as ${data.platform.subject}` : 'Not linked'}</span>
          </div>

          <div className="ez-view-as__footer">
            <button
              type="button"
              className="ez-view-as__cancel"
              data-testid="admin-user-profile-close"
              onClick={onClose}
            >
              Close
            </button>
            {/* Absent, never disabled, for anyone who may not open a view (F8 §8). */}
            {canViewAsUser && data.role !== 'superadmin' && !data.isDeleted ? (
              <button
                type="button"
                className="ez-view-as__confirm"
                data-testid="admin-user-profile-view-as"
                onClick={() => onViewAs(data)}
              >
                View as {data.name}
              </button>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════ Directory → Invitations (§6.3) */

export function InvitationsScreen({
  source,
  onInvite,
}: {
  source: AdminSource;
  onInvite?: () => void;
}): React.ReactElement {
  const [tab, setTab] = useState<'sent' | 'pending'>('sent');
  const { data, reload } = useAsync(() => source.invitations(tab), [tab]);
  const rows: InvitationRow[] = data?.rows ?? [];
  const write = useAdminWrite();
  /**
   * P-251 — B3's *"Resend is disabled for 60 s"*. The service refuses inside the window with a
   * 429 and the screen used to let the click through anyway, so the refusal only reached the
   * person through `app/errors.ts`' window-level unhandled-rejection net. A tick every second
   * re-renders the row so the button re-enables the moment the window closes, without a reload.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);
  /*
   * P-012. **Revoke** used to fire on the first click. monday's own confirmation for a
   * destructive row is a 500 × 140 modal — title as a question, one sentence saying what
   * happens next, `Cancel` then the verb (`discovery/ui-reference/confirmations/item-delete`,
   * captured on the sandbox this round) — which is the shape `ConfirmDialog` already has.
   */
  const confirm = useConfirm();

  return (
    <div data-testid="admin-invitations">
      <h1 className="ez-admin__heading">Invitations</h1>
      <div className="ez-admin__toolbar" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'pending'}
          onClick={() => setTab('pending')}
        >
          Pending your approval / {tab === 'pending' ? data?.total ?? 0 : 0}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'sent'} onClick={() => setTab('sent')}>
          Sent invitations / {tab === 'sent' ? data?.total ?? 0 : 0}
        </button>
        <span style={{ flex: 1 }} />
        <button type="button" onClick={onInvite} data-testid="admin-invite-button">
          Invite
        </button>
      </div>

      <div className="ez-admin-table__scroll">
        <table className="ez-admin-table ez-admin-invitations" data-testid="admin-invitations-table">
          <colgroup>
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Invited by</th>
              <th>Date</th>
              <th>Products</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td title={row.email}>{row.email}</td>
                <td>{TITLE_CASE[row.role] ?? row.role}</td>
                <td>{row.invitedBy?.name ?? ''}</td>
                <td>{shortDate(row.createdAt)}</td>
                <td>{row.product}</td>
                <td>
                  {(() => {
                    const availableAt = row.resendAvailableAt
                      ? Date.parse(row.resendAvailableAt)
                      : 0;
                    const waitSeconds = Math.max(0, Math.ceil((availableAt - now) / 1000));
                    const sendable = row.state === 'sent' || row.state === 'expired';
                    return (
                      <button
                        type="button"
                        data-testid={`admin-invitation-resend-${row.id}`}
                        title={
                          waitSeconds > 0
                            ? `Sent just now. You can resend in ${waitSeconds}s.`
                            : 'Send the invitation again with a new link'
                        }
                        onClick={() => {
                          void write(() => source.resendInvite(row.id), {
                            success: `Invitation resent to ${row.email}`,
                            after: reload,
                          });
                        }}
                        disabled={!sendable || waitSeconds > 0}
                      >
                        {/* The countdown lives in the `title`, never in the label: a label
                            that changes width every second moves the row's other buttons. */}
                        Resend
                      </button>
                    );
                  })()}{' '}
                  <button
                    type="button"
                    onClick={() => {
                      void (async () => {
                        const ok = await confirm({
                          title: `Revoke the invitation to ${row.email}?`,
                          body: 'The link we e-mailed stops working immediately. You can invite them again at any time.',
                          confirmLabel: 'Revoke',
                          danger: true,
                        });
                        if (!ok) return;
                        await write(() => source.revokeInvite(row.id), {
                          success: `Invitation to ${row.email} revoked`,
                          after: reload,
                        });
                      })();
                    }}
                    disabled={row.state !== 'sent'}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 ? (
        <div data-testid="admin-invitations-empty" style={{ padding: '48px 0', textAlign: 'center' }}>
          <p style={{ font: '500 16px/1.4 system-ui', margin: 0 }}>No sent invitations yet</p>
          <p className="ez-admin__subheading" style={{ marginTop: 4 }}>
            {data?.emptyReason ??
              "Once sent, invites will show up here until they're accepted."}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The people multi-select behind *Create new team* (P-249) and the team pane's
 * *+ Add members* (P-249's companion). It reads `admin.sharing.people`, which is the picker
 * every share dialog already uses — P1 §5.1's rule that P2 adds UI, not a second read path.
 *
 * Debounced 200 ms, like the share dialog's.
 */
function PeoplePicker({
  source,
  label,
  chosen,
  onChange,
  exclude,
  testId,
}: {
  source: AdminSource;
  label: string;
  chosen: PeopleSearchRow[];
  onChange: (next: PeopleSearchRow[]) => void;
  exclude?: ReadonlySet<string>;
  testId: string;
}): React.ReactElement {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PeopleSearchRow[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void source
        .people({ query })
        .then(setResults)
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(timer);
  }, [source, query]);

  const chosenIds = new Set(chosen.map((row) => row.id));
  const inputId = `${testId}-input`;

  return (
    <div className="ez-admin-field" data-testid={testId}>
      <label htmlFor={inputId}>{label}</label>
      <div className="ez-admin-people">
        <div className="ez-admin-people__chosen" data-testid={`${testId}-chosen`}>
          {chosen.map((row) => (
            <span key={row.id} className="ez-admin-chip">
              {row.name}
              <button
                type="button"
                aria-label={`Remove ${row.name}`}
                onClick={() => onChange(chosen.filter((entry) => entry.id !== row.id))}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
        <input
          id={inputId}
          type="search"
          placeholder="Search by name or email address"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {query.length > 0 ? (
          <div className="ez-admin-people__results" data-testid={`${testId}-results`}>
            {results
              .filter((row) => row.kind === 'user' && !chosenIds.has(row.id) && !exclude?.has(row.id))
              .slice(0, 8)
              .map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => {
                    onChange([...chosen, row]);
                    setQuery('');
                  }}
                >
                  {row.name}
                  <span className="ez-admin__hint">{row.email ?? ''}</span>
                </button>
              ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════ Directory → Teams (§6.4) */

export function TeamsScreen({ source }: { source: AdminSource }): React.ReactElement {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  /** P-249 — C2's *pre-filled* dialog: the picker, and who is in it. */
  const [newMembers, setNewMembers] = useState<PeopleSearchRow[]>([]);
  /** P-249's companion — *+ Add members* on the team pane. */
  const [addingMembers, setAddingMembers] = useState<PeopleSearchRow[] | null>(null);
  /** P-247 — inline rename on the team's heading. */
  const [renaming, setRenaming] = useState<string | null>(null);
  const write = useAdminWrite();
  const { data: teams, reload } = useAsync<TeamSummary[]>(() => source.teams(), []);
  const first = teams?.[0]?.id ?? null;
  const activeId = selectedId ?? first;
  const { data: team, reload: reloadTeam } = useAsync<TeamDetail | undefined>(
    () => (activeId ? source.team(activeId) : Promise.resolve(undefined)),
    [activeId],
  );
  /*
   * P-012. Taking someone's ownership away is the same family as taking an admin's rights
   * away, which M8-P2 §6.4 already says must be confirmed; it was the one that acted on the
   * first click. *Granting* ownership is not destructive and stays instant.
   */
  const confirm = useConfirm();

  return (
    <div data-testid="admin-teams">
      <h1 className="ez-admin__heading">Teams</h1>
      <p className="ez-admin__subheading">
        Organize your account users into teams and assign them to content.
      </p>
      <div className="ez-admin__toolbar">
        <button type="button" onClick={() => setCreating(true)} data-testid="admin-new-team">
          New team
        </button>
      </div>

      {creating ? (
        <div role="dialog" aria-label="Create new team" className="ez-admin-card" data-testid="admin-create-team">
          <h3>Create new team</h3>
          <div className="ez-admin-field">
            <label htmlFor="ez-team-name">Team name</label>
            <input
              id="ez-team-name"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              style={{ height: 32, padding: '0 8px', borderRadius: 4 }}
            />
          </div>
          {/*
            * P-249 — C2 describes monday's dialog as **pre-filled**. The creator is added as a
            * member and an owner by the service whatever this picker holds, so it starts empty
            * and names everybody *else* the team starts with.
            */}
          <PeoplePicker
            source={source}
            label="Members"
            testId="admin-create-team-members"
            chosen={newMembers}
            onChange={setNewMembers}
          />
          <p>Allow guests on this team — not supported in Eli Z Monday.</p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setNewMembers([]);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={newName.trim().length === 0}
              onClick={() => {
                const name = newName.trim();
                void write(
                  () => source.createTeam({ name, memberIds: newMembers.map((row) => row.id) }),
                  {
                    success: `Team ${name} created`,
                    after: () => {
                      setCreating(false);
                      setNewName('');
                      setNewMembers([]);
                      reload();
                    },
                  },
                );
              }}
            >
              Create
            </button>
          </div>
        </div>
      ) : null}

      {/* The class is for `mobile.css` only (F9-D25); the inline grid is the measured desktop one. */}
      <div className="ez-admin-teams" style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 32 }}>
        <div>
          <div className="ez-admin-filter__title">All teams / {teams?.length ?? 0}</div>
          {(teams ?? []).map((row) => (
            <button
              key={row.id}
              type="button"
              className="ez-admin__rail-item"
              style={{ width: '100%' }}
              aria-current={row.id === activeId ? 'page' : undefined}
              onClick={() => setSelectedId(row.id)}
            >
              {row.name}
              <span style={{ marginLeft: 'auto', opacity: 0.6 }}>{row.memberCount}</span>
            </button>
          ))}
        </div>

        {team ? (
          <div data-testid="admin-team-detail">
            {/* P-247 — rename in place; `admin.teams.update` and its `team.updated` audit row
                already existed and nothing on screen reached them. */}
            {renaming === team.id ? (
              <form
                className="ez-admin-field"
                onSubmit={(event) => {
                  event.preventDefault();
                  const next = new FormData(event.currentTarget).get('teamName');
                  const name = typeof next === 'string' ? next.trim() : '';
                  if (name.length === 0 || name === team.name) {
                    setRenaming(null);
                    return;
                  }
                  const was = team.name;
                  void write(() => source.updateTeam({ teamId: team.id, name }), {
                    success: `Team renamed to ${name}`,
                    undo: () => source.updateTeam({ teamId: team.id, name: was }),
                    after: () => {
                      setRenaming(null);
                      reload();
                      reloadTeam();
                    },
                  });
                }}
              >
                <input
                  name="teamName"
                  aria-label="Team name"
                  defaultValue={team.name}
                  data-testid="admin-team-rename-input"
                  autoFocus
                />
                <button type="submit">Save</button>
                <button type="button" onClick={() => setRenaming(null)}>
                  Cancel
                </button>
              </form>
            ) : (
              <h2 style={{ font: '700 20px/1.3 system-ui', margin: '0 0 4px 0' }}>{team.name}</h2>
            )}
            <p className="ez-admin__subheading">{team.memberCount} members</p>
            <div className="ez-admin__toolbar">
              <span className="ez-admin-chip">Users: {team.memberCount}</span>
              <span className="ez-admin-chip">Content: {team.contentCount}</span>
              <span style={{ flex: 1 }} />
              <button
                type="button"
                data-testid="admin-team-add-members"
                onClick={() => setAddingMembers((current) => (current === null ? [] : null))}
              >
                + Add members
              </button>
              <button type="button" data-testid="admin-team-rename" onClick={() => setRenaming(team.id)}>
                Rename
              </button>
              {/*
                * P-247 — the soft delete M8-P2 C4 specifies: the team goes, its subscriptions
                * go with it, and a `team` cell value still resolves its name. Destructive, so
                * it says all three before it happens.
                */}
              <button
                type="button"
                className="ez-admin__danger"
                data-testid="admin-team-delete"
                onClick={() => {
                  void (async () => {
                    const ok = await confirm({
                      title: `Delete the team ${team.name}?`,
                      body:
                        `Its ${team.memberCount} members keep their accounts and their own board ` +
                        'access. The team is removed from every board and workspace it was ' +
                        'subscribed to, and from the people pickers. Anywhere it was already ' +
                        'written into a cell, its name still reads.',
                      confirmLabel: 'Delete team',
                      danger: true,
                    });
                    if (!ok) return;
                    await write(() => source.deleteTeam(team.id), {
                      success: `Team ${team.name} deleted`,
                      after: () => {
                        setSelectedId(null);
                        reload();
                      },
                    });
                  })();
                }}
              >
                Delete team
              </button>
            </div>

            {addingMembers !== null ? (
              <div className="ez-admin-card" data-testid="admin-team-add-members-panel">
                <PeoplePicker
                  source={source}
                  label={`Add people to ${team.name}`}
                  testId="admin-team-member-picker"
                  chosen={addingMembers}
                  exclude={new Set(team.members.map((member) => member.userId))}
                  onChange={setAddingMembers}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" onClick={() => setAddingMembers(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={addingMembers.length === 0}
                    onClick={() => {
                      const userIds = addingMembers.map((row) => row.id);
                      void write(() => source.addTeamMembers({ teamId: team.id, userIds }), {
                        success: `${userIds.length} added to ${team.name}`,
                        undo: () => source.removeTeamMembers({ teamId: team.id, userIds }),
                        after: () => {
                          setAddingMembers(null);
                          reload();
                          reloadTeam();
                        },
                      });
                    }}
                  >
                    Add
                  </button>
                </div>
              </div>
            ) : null}
            <div className="ez-admin-table__scroll">
              <table className="ez-admin-table ez-admin-team-members" data-testid="admin-team-members">
                <colgroup>
                  <col className="c-name" />
                  <col className="c-email" />
                  <col className="c-title" />
                  <col className="c-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Title</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {team.members.map((member) => (
                    <tr key={member.userId}>
                      <td>
                        {member.name}
                        {member.isOwner ? <span className="ez-admin-chip">owner</span> : null}
                      </td>
                      <td>{member.email}</td>
                      <td>{member.title ?? ''}</td>
                      <td>
                        <button
                          type="button"
                          aria-label={member.isOwner ? `Remove owner ${member.name}` : `Make ${member.name} an owner`}
                          onClick={() => {
                            void (async () => {
                              if (member.isOwner) {
                                const ok = await confirm({
                                  title: `Remove ${member.name} as an owner of ${team.name}?`,
                                  body: 'They stay in the team and keep their account role; they can no longer manage its members.',
                                  confirmLabel: 'Remove owner',
                                  danger: true,
                                });
                                if (!ok) return;
                              }
                              await write(
                                () =>
                                  source.setTeamOwner({
                                    teamId: team.id,
                                    userId: member.userId,
                                    isOwner: !member.isOwner,
                                  }),
                                {
                                  success: member.isOwner
                                    ? `${member.name} is no longer an owner`
                                    : `${member.name} is an owner of ${team.name}`,
                                  undo: () =>
                                    source.setTeamOwner({
                                      teamId: team.id,
                                      userId: member.userId,
                                      isOwner: member.isOwner,
                                    }),
                                  after: reloadTeam,
                                },
                              );
                            })();
                          }}
                        >
                          {member.isOwner ? 'Owner' : 'Make owner'}
                        </button>{' '}
                        <button
                          type="button"
                          aria-label={`Remove ${member.name}`}
                          onClick={() => {
                            void write(
                              () =>
                                source.removeTeamMembers({ teamId: team.id, userIds: [member.userId] }),
                              {
                                success: `${member.name} removed from ${team.name}`,
                                undo: () =>
                                  source.addTeamMembers({ teamId: team.id, userIds: [member.userId] }),
                                after: () => {
                                  reload();
                                  reloadTeam();
                                },
                              },
                            );
                          }}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ══════════════════════════════ General → Account and Customization (§6.5) */

export function GeneralScreen({ source }: { source: AdminSource }): React.ReactElement {
  const { data, reload } = useAsync<PublicAccount>(() => source.account(), []);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<PublicAccount> | null>(null);
  const value: PublicAccount | undefined =
    data === undefined ? undefined : ({ ...data, ...(draft ?? {}) } as PublicAccount);

  const toast = useOptionalToast();
  /**
   * P-242 / P-248. Every General and Customization control writes through this one function, so
   * this is where the account's settings grew a toast and an **Undo**: the inverse patch is
   * derived from the copy of `PublicAccount` the screen is already holding, and replayed
   * through the same write. The inline `admin-general-error` line stays — G4 asserts the api's
   * contrast sentence appears next to the field that caused it, which a toast cannot do.
   */
  const save = useCallback(
    async (patch: Record<string, unknown>, options: { undoable?: boolean; label?: string } = {}) => {
      const before = data;
      try {
        await source.updateAccount(patch);
        setError(null);
        setDraft(null);
        const undo =
          options.undoable === false || before === undefined
            ? undefined
            : inverseAccountPatch(before, patch);
        toast(options.label ?? `${describeAccountPatch(patch)} saved`, {
          kind: 'positive',
          ...(undo
            ? {
                action: {
                  label: 'Undo',
                  onClick: () => {
                    void source
                      .updateAccount(undo)
                      .then(() => reload())
                      .catch(() => undefined);
                  },
                },
              }
            : {}),
        });
        reload();
      } catch (cause: unknown) {
        const message = cause instanceof Error ? cause.message : String(cause);
        setError(message);
        toast(message, { kind: 'negative' });
      }
    },
    [source, reload, data, toast],
  );

  if (value === undefined) return <div data-testid="admin-general" />;

  return (
    <div data-testid="admin-general">
      <h1 className="ez-admin__heading">Account</h1>
      {error ? (
        <p role="alert" data-testid="admin-general-error">
          {error}
        </p>
      ) : null}

      <div className="ez-admin-card">
        <h3>Account</h3>
        <div className="ez-admin-field">
          <label htmlFor="ez-account-name">Account name</label>
          <input
            id="ez-account-name"
            value={value.name}
            onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            onBlur={(event) => void save({ name: event.target.value })}
          />
        </div>
        <div className="ez-admin-field">
          <label htmlFor="ez-product-name">Product name</label>
          <input
            id="ez-product-name"
            data-testid="admin-product-name"
            value={value.productName}
            onChange={(event) => setDraft({ ...draft, productName: event.target.value })}
            onBlur={(event) => void save({ productName: event.target.value })}
          />
        </div>
      </div>

      <div className="ez-admin-card">
        <h3>Default workspace</h3>
        <p>
          Where everyone lands when they open {value.productName} and have not chosen a workspace
          themselves. Change it from the workspace&rsquo;s own menu.
        </p>
        <div className="ez-admin-field">
          <span data-testid="admin-default-workspace">
            {value.defaultWorkspace?.name ?? 'No default workspace'}
          </span>
        </div>
      </div>

      <div className="ez-admin-card">
        <h3>Weekends</h3>
        <p>
          This will appear across all date-related features (like timeline columns and calendar
          views) and affect calculations (like task duration).
        </p>
        {(['friday-saturday', 'saturday-sunday'] as const).map((option) => (
          <label key={option} className="ez-admin-field" style={{ display: 'flex' }}>
            <input
              type="radio"
              name="ez-weekend"
              checked={value.weekendDays === option}
              onChange={() => void save({ weekendDays: option })}
            />
            {option === 'friday-saturday' ? 'Friday – Saturday' : 'Saturday – Sunday'}
          </label>
        ))}
        <h3 style={{ marginTop: 16 }}>Show weekends on calendars?</h3>
        <label className="ez-admin-field" style={{ display: 'flex' }}>
          <input
            type="radio"
            name="ez-show-weekends"
            checked={value.showWeekends}
            onChange={() => void save({ showWeekends: true })}
          />
          Show weekends and include them in calculations, like task duration
        </label>
        <label className="ez-admin-field" style={{ display: 'flex' }}>
          <input
            type="radio"
            name="ez-show-weekends"
            checked={!value.showWeekends}
            onChange={() => void save({ showWeekends: false })}
          />
          Hide weekends and exclude them from calculations
        </label>
      </div>

      <h1 className="ez-admin__heading" style={{ marginTop: 32 }}>
        Customization
      </h1>

      <div className="ez-admin-card">
        <h3>Main menu logo</h3>
        <p>Recommended: 40x40px PNG with transparent background</p>
        <div className="ez-admin-field">
          <label htmlFor="ez-logo-url">Logo URL</label>
          <input
            id="ez-logo-url"
            value={value.logoUrl ?? ''}
            onChange={(event) => setDraft({ ...draft, logoUrl: event.target.value })}
            onBlur={(event) => void save({ logoUrl: event.target.value || null })}
          />
          <button type="button" onClick={() => void save({ logoUrl: null })}>
            Clear
          </button>
        </div>
        <h3 style={{ marginTop: 16 }}>Wordmark</h3>
        <p>Shown in the top bar when no logo image is set.</p>
        <div className="ez-admin-field">
          <label htmlFor="ez-wordmark">Wordmark</label>
          <input
            id="ez-wordmark"
            value={value.wordmark ?? ''}
            onChange={(event) => setDraft({ ...draft, wordmark: event.target.value })}
            onBlur={(event) => void save({ wordmarkText: event.target.value || null })}
          />
        </div>
      </div>

      <div className="ez-admin-card">
        <h3>Brand colour</h3>
        <p>Applied to the top bar and every primary button, in every open tab, without a reload.</p>
        <div className="ez-admin-field">
          <label htmlFor="ez-brand-primary">Primary</label>
          <input
            id="ez-brand-primary"
            data-testid="admin-brand-primary"
            value={value.brand.primary}
            onChange={(event) =>
              setDraft({ ...draft, brand: { ...value.brand, primary: event.target.value } })
            }
            onBlur={(event) => void save({ brandPrimary: event.target.value })}
          />
        </div>
        <div className="ez-admin-field">
          <label htmlFor="ez-brand-on">Text on primary</label>
          <input
            id="ez-brand-on"
            value={value.brand.onPrimary}
            onChange={(event) =>
              setDraft({ ...draft, brand: { ...value.brand, onPrimary: event.target.value } })
            }
            onBlur={(event) => void save({ brandTextOnPrimary: event.target.value })}
          />
        </div>
        <div className="ez-brand-preview" data-testid="admin-brand-preview">
          <div
            className="ez-brand-preview__bar"
            style={{ background: value.brand.primary, color: value.brand.onPrimary }}
          >
            {value.wordmark ?? value.productName}
          </div>
          <button
            type="button"
            className="ez-brand-preview__button"
            style={{ background: value.brand.primary, color: value.brand.onPrimary }}
          >
            Primary button
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════ Permissions (§6.6) */

const CAPABILITY_GROUPS: Array<{ group: string; prefix: string[] }> = [
  { group: 'Account', prefix: ['account.'] },
  { group: 'API', prefix: ['api_token.'] },
  { group: 'Workspaces and boards', prefix: ['workspace.', 'board.', 'dashboard.'] },
  { group: 'Search', prefix: ['search.'] },
];

export function PermissionsScreen({ source }: { source: AdminSource }): React.ReactElement {
  const { data, reload } = useAsync<{ cells: CapabilityCell[] }>(() => source.capabilities(), []);
  const cells = data?.cells ?? [];
  const actions = [...new Set(cells.map((cell) => cell.action))];

  /*
   * **A capability override must survive two clicks in a row** (round 7e, and the round-7c
   * incident that left overrides flipped on staging).
   *
   * Each box was `checked={cell.allowed}` with `onChange` firing a write and a `reload()` —
   * a fully controlled input whose only source of truth is a server round-trip. Click it
   * twice quickly and the two writes for the same cell are in flight together: the browser
   * has already flipped the DOM, React has not re-rendered, and whichever write the api
   * commits last decides the value. The sweep caught it as `toggle-not-restored` on
   * `dashboard.create for member` — a setting turned off and not turned back on.
   *
   * Two things fix it, and both are what a person wants anyway:
   *   - `pending` holds the **intent** for a cell, so the box shows what you just clicked
   *     instead of flickering back until the refetch lands;
   *   - `inFlight` chains the writes for one cell, so the second is sent after the first has
   *     been answered and the last click always wins.
   * An override is dropped as soon as the reloaded matrix agrees with it.
   */
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const inFlight = useRef(new Map<string, Promise<unknown>>());
  /** Cells whose write has been answered and are waiting for the next matrix to arrive. */
  const settled = useRef(new Set<string>());

  /*
   * An intent lives exactly until the first matrix that arrives **after** its write was
   * answered — whatever that matrix says. Dropping it on *agreement* instead would leave the
   * box showing the intent for ever when the server declines the change (the `/__ui/admin`
   * fixture declines every one of them), which is a screen that lies.
   */
  useEffect(() => {
    if (!data || settled.current.size === 0) return;
    const done = [...settled.current];
    settled.current.clear();
    setPending((current) => {
      let next = current;
      for (const key of done) {
        if (key in next) {
          if (next === current) next = { ...current };
          delete next[key];
        }
      }
      return next;
    });
  }, [data]);

  /** P-242: a matrix cell says what it did, like every other write in the product. */
  const toast = useOptionalToast();
  const setCapability = useCallback(
    (action: string, role: AccountRole, allowed: boolean): void => {
      const key = `${action}:${role}`;
      setPending((current) => ({ ...current, [key]: allowed }));
      const queued = (inFlight.current.get(key) ?? Promise.resolve())
        .catch(() => undefined)
        .then(() => source.setCapability({ action, role, allowed }))
        .then(() => {
          settled.current.add(key);
          toast(`${action} for ${TITLE_CASE[role] ?? role}: ${allowed ? 'allowed' : 'not allowed'}`, {
            kind: 'positive',
          });
          reload();
        })
        .catch((cause: unknown) => {
          // The write failed: the box goes back to whatever the server says it is.
          settled.current.add(key);
          toast(cause instanceof Error ? cause.message : String(cause), { kind: 'negative' });
          reload();
        });
      inFlight.current.set(key, queued);
    },
    [source, reload, toast],
  );

  return (
    <div data-testid="admin-permissions">
      <h1 className="ez-admin__heading">Permissions</h1>
      <p className="ez-admin__subheading">
        Define the default account-level permissions for your users. The defaults, and every cell
        marked fixed, live in <code>specs/M0-F8-permissions.md</code> §5.3.
      </p>
      <div className="ez-admin-table__scroll">
        <table className="ez-admin-table ez-admin-matrix" data-testid="admin-permissions-matrix">
          <colgroup>
            <col className="c-action" />
            <col className="c-role" />
            <col className="c-role" />
            <col className="c-role" />
            <col className="c-role" />
          </colgroup>
          <thead>
            <tr>
              <th>Default account roles</th>
              {ROLES.map((role) => (
                <th key={role} className="c-role">
                  {TITLE_CASE[role]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CAPABILITY_GROUPS.map((group) => {
              const groupActions = actions.filter((action) =>
                group.prefix.some((prefix) => action.startsWith(prefix)),
              );
              if (groupActions.length === 0) return null;
              return (
                <>
                  <tr key={group.group} className="ez-admin-matrix__group">
                    <td colSpan={5}>{group.group}</td>
                  </tr>
                  {groupActions.map((action) => (
                    <tr key={action} data-testid={`admin-cap-${action}`}>
                      <td>{action}</td>
                      {ROLES.map((role) => {
                        const cell = cells.find((c) => c.action === action && c.role === role);
                        if (!cell) return <td key={role} className="c-role" />;
                        return (
                          <td key={role} className="c-role">
                            {cell.configurable ? (
                              <input
                                type="checkbox"
                                aria-label={`${action} for ${role}`}
                                checked={pending[`${action}:${role}`] ?? cell.allowed}
                                onChange={(event) => setCapability(action, role, event.target.checked)}
                              />
                            ) : (
                              <span
                                className="ez-admin-matrix__fixed"
                                title="Fixed by the permission model"
                              >
                                {cell.allowed ? '✔' : '✖'}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════ Security (§6.7) */

export function SecurityScreen({ source }: { source: AdminSource }): React.ReactElement {
  const { data, reload } = useAsync<SecuritySettings>(() => source.security(), []);
  /**
   * M8-P3 §7 — **Active impersonations**. It sits on Security rather than on Users because it
   * answers a security question ("is anybody inside somebody else's account right now?"), and
   * it is the only place a view opened from a browser nobody has any more can be closed.
   *
   * It fails soft: an ordinary admin is refused the query, and the card simply does not paint.
   */
  const { data: impersonations, reload: reloadImpersonations } = useAsync<ActiveImpersonationRow[]>(
    () => source.activeImpersonations().catch(() => [] as ActiveImpersonationRow[]),
    [],
  );
  /** P-242 / P-248: hooks before the early return; a security save says so, and is undoable. */
  const write = useAdminWrite();
  const confirm = useConfirm();
  const previous = data;
  if (!data) return <div data-testid="admin-security" />;
  const save = (patch: Record<string, unknown>, label: string): void => {
    const back = inverseSecurityPatch(previous, patch);
    void write(() => source.updateSecurity(patch), {
      success: `${label} saved`,
      ...(back ? { undo: () => source.updateSecurity(back) } : {}),
      after: reload,
    });
  };

  return (
    <div data-testid="admin-security">
      <h1 className="ez-admin__heading">Security</h1>
      <p className="ez-admin__subheading">
        Use these settings to manage how people sign up and log in to Eli Z Monday.
      </p>

      {impersonations ? (
        <div className="ez-admin-card" data-testid="admin-security-impersonations">
          <h3>Active impersonations</h3>
          <p className="ez-admin__hint">
            A super admin viewing the product as somebody else. The view is read-only, is
            recorded in the audit log, and ends by itself after two hours.
          </p>
          <ActiveImpersonationsView
            rows={impersonations}
            onEnd={(sessionId) => {
              void source.endImpersonation(sessionId).then(reloadImpersonations);
            }}
          />
        </div>
      ) : null}

      {/* M0-F3b §8.2 — the switches that decide how everybody in this account signs in. */}
      <div className="ez-admin-card" data-testid="admin-security-sso">
        <h3>{data.sso.label.replace(/^Sign in with /, '')} SSO</h3>
        <p className="ez-admin__hint">
          {data.sso.configured
            ? 'People sign in with their Eli Z Platform account. Who is allowed through is managed on the platform itself.'
            : 'This server does not hold the platform OAuth client credentials, so the button is hidden and the two sign-in routes are inactive.'}
        </p>
        <div className="ez-admin-field">
          <label htmlFor="ez-sso-enabled">Enabled</label>
          <input
            id="ez-sso-enabled"
            type="checkbox"
            data-testid="admin-sso-enabled"
            disabled={!data.sso.configured}
            defaultChecked={data.sso.enabled}
            onChange={(event) => save({ sso: { enabled: event.target.checked } }, 'Single sign-on')}
          />
        </div>
        <div className="ez-admin-field">
          <label htmlFor="ez-sso-password">Allow e-mail and password login</label>
          <input
            id="ez-sso-password"
            type="checkbox"
            data-testid="admin-sso-password"
            defaultChecked={data.sso.allowPasswordLogin}
            onChange={(event) => save({ sso: { allowPasswordLogin: event.target.checked } }, 'Password sign-in')}
          />
        </div>
        <p className="ez-admin__hint">
          With this off, the login page offers only the platform button. If the platform ever
          becomes unreachable, an administrator on the server can still mint a one-time sign-in
          link with <code>pnpm auth:bootstrap-admin</code>.
        </p>
        <div className="ez-admin-field">
          <label htmlFor="ez-sso-provision">Create an account on first platform sign-in</label>
          <input
            id="ez-sso-provision"
            type="checkbox"
            data-testid="admin-sso-provision"
            defaultChecked={data.sso.autoProvision}
            onChange={(event) => save({ sso: { autoProvision: event.target.checked } }, 'Auto-provisioning')}
          />
        </div>
        <p className="ez-admin__hint">
          New people get the default account role below. With this off, somebody who signs in
          with the platform but has no account here is turned away and has to be invited first.
        </p>
      </div>

      <div className="ez-admin-card">
        <h3>Password policy</h3>
        <div className="ez-admin-field">
          <label htmlFor="ez-pw-min">Minimum length</label>
          <input
            id="ez-pw-min"
            type="number"
            min={8}
            max={64}
            defaultValue={data.passwordMinLength}
            onBlur={(event) => save({ passwordMinLength: Number(event.target.value) }, 'Minimum password length')}
          />
        </div>
        <div className="ez-admin-field">
          <label htmlFor="ez-pw-common">Reject common passwords</label>
          <input
            id="ez-pw-common"
            type="checkbox"
            defaultChecked={data.passwordRejectCommon}
            onChange={(event) => save({ passwordRejectCommon: event.target.checked }, 'Password policy')}
          />
        </div>
      </div>

      <div className="ez-admin-card">
        <h3>Sessions</h3>
        <div className="ez-admin-field">
          <label htmlFor="ez-session-idle">Idle days</label>
          <input
            id="ez-session-idle"
            type="number"
            min={1}
            max={90}
            defaultValue={data.sessionIdleDays}
            onBlur={(event) => save({ sessionIdleDays: Number(event.target.value) }, 'Idle days')}
          />
        </div>
        <div className="ez-admin-field">
          <label htmlFor="ez-session-abs">Absolute days</label>
          <input
            id="ez-session-abs"
            type="number"
            min={1}
            max={365}
            defaultValue={data.sessionAbsoluteDays}
            onBlur={(event) => save({ sessionAbsoluteDays: Number(event.target.value) }, 'Absolute days')}
          />
        </div>
        {/*
          * §6.7 / acceptance E4 (P-246). Ends every live session on the account **except this
          * one** — an admin who signs themselves out with everybody else cannot undo it — and
          * the sockets go with them. Destructive, so it asks first (P-012's shape).
          */}
        <div className="ez-admin-field">
          <button
            type="button"
            className="ez-admin__danger"
            data-testid="admin-logout-everyone"
            onClick={() => {
              void (async () => {
                const ok = await confirm({
                  title: 'Log everyone out?',
                  body:
                    'Everybody signs in again the next time they use Eli Z Monday, on every ' +
                    'device. Your own session is kept, so you stay signed in here. This cannot ' +
                    'be undone.',
                  confirmLabel: 'Log out everyone',
                  danger: true,
                });
                if (!ok) return;
                await write(() => source.logoutEveryone(), {
                  success: 'Everyone has been logged out',
                  after: reload,
                });
              })();
            }}
          >
            Log out everyone
          </button>
          <span className="ez-admin__hint">
            Ends every session but yours. People stay in the account; they sign in again.
          </span>
        </div>
      </div>

      <div className="ez-admin-card">
        <h3>Sign-in</h3>
        <div className="ez-admin-field">
          <label htmlFor="ez-device">Emailed device verification</label>
          <input
            id="ez-device"
            type="checkbox"
            defaultChecked={data.deviceVerificationEnabled}
            onChange={(event) => save({ deviceVerificationEnabled: event.target.checked }, 'Device verification')}
          />
        </div>
        <div className="ez-admin-field">
          <label htmlFor="ez-domains">Self-join domains</label>
          <input
            id="ez-domains"
            defaultValue={data.autoJoinDomains.join(', ')}
            onBlur={(event) =>
              save(
                {
                  autoJoinDomains: event.target.value
                    .split(/[\s,]+/)
                    .map((value) => value.trim())
                    .filter(Boolean),
                },
                'Self-join domains',
              )
            }
          />
        </div>
      </div>

      {data.planned.map((item) => (
        <div key={item.feature} className="ez-admin-card ez-admin-card--planned">
          <h3>{item.feature}</h3>
          <p>
            {item.milestone === 'not planned'
              ? 'Not planned for Eli Z Monday.'
              : `Planned for ${item.milestone}.`}
          </p>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════ Tokens, audit and usage (§6.8) */

export function TokensScreen({ source }: { source: AdminSource }): React.ReactElement {
  const { data, reload } = useAsync<{ rows: TokenOverviewRow[] }>(() => source.tokens(), []);
  const write = useAdminWrite();
  const confirm = useConfirm();
  return (
    <div data-testid="admin-tokens">
      <h1 className="ez-admin__heading">API tokens</h1>
      <p className="ez-admin__subheading">
        A personal token carries exactly its owner&apos;s permissions. Token values are never shown
        here — only their owner can reveal one, on their own Developers page.
      </p>
      <div className="ez-admin-table__scroll">
        <table className="ez-admin-table" data-testid="admin-tokens-table">
          <thead>
            <tr>
              <th>Owner</th>
              <th>Name</th>
              <th>Kind</th>
              <th>Created</th>
              <th>Last used</th>
              <th>Last API version</th>
              <th>Last user agent</th>
              {/* P-245 — the action column §6.8 specifies. Unlabelled, so the seven captured
                  headers above stay exactly as they were measured. */}
              <th />
            </tr>
          </thead>
          <tbody>
            {(data?.rows ?? []).map((row) => (
              <tr key={row.id}>
                <td>{row.owner.name}</td>
                <td>{row.name}</td>
                <td>{row.kind}</td>
                <td>{shortDate(row.createdAt)}</td>
                <td>{shortDate(row.lastUsedAt)}</td>
                <td>{row.lastSeenApiVersion ?? ''}</td>
                <td title={row.lastSeenUserAgent ?? ''}>{row.lastSeenUserAgent ?? ''}</td>
                <td>
                  {row.revokedAt === null ? (
                    <button
                      type="button"
                      className="ez-admin__danger"
                      data-testid={`admin-token-revoke-${row.id}`}
                      onClick={() => {
                        void (async () => {
                          const ok = await confirm({
                            title: `Revoke ${row.owner.name}'s token “${row.name}”?`,
                            body:
                              'Anything using this token stops working immediately, with a 401. ' +
                              'It cannot be un-revoked; they can mint a new one on their own ' +
                              'Developers page.',
                            confirmLabel: 'Revoke token',
                            danger: true,
                          });
                          if (!ok) return;
                          await write(() => source.revokeToken(row.id), {
                            success: `“${row.name}” revoked`,
                            after: reload,
                          });
                        })();
                      }}
                    >
                      Revoke
                    </button>
                  ) : (
                    <span className="ez-admin-chip ez-admin-chip--inactive">revoked</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * §6.8 — *"filters by event (grouped: auth, users, account, boards)"* (P-243).
 *
 * The vocabulary itself is the api's (`modules/auth/audit-events.ts` for the account half,
 * monday's own `activity_logs.event` strings for the board half); this module never imports
 * server code, so the grouping is written out here. A row whose event is not in this list is
 * still read and still rendered — the picker narrows, it never hides.
 */
const AUDIT_EVENT_GROUPS: Array<{ group: string; events: string[] }> = [
  {
    group: 'Sign-in',
    events: [
      'user.login_succeeded',
      'user.login_failed',
      'user.login_throttled',
      'user.logged_out',
      'user.sessions_revoked',
      'device.verification_sent',
      'device.verification_failed',
      'device.trusted',
      'sso.login_succeeded',
      'sso.login_refused',
    ],
  },
  {
    group: 'Users',
    events: [
      'user.invited',
      'user.invitation_resent',
      'user.invitation_revoked',
      'user.invitation_accepted',
      'user.role_changed',
      'user.activated',
      'user.deactivated',
      'user.profile_updated',
      'user.password_changed',
      'user.password_reset_requested',
      'user.password_reset_completed',
      'sso.identity_linked',
      'sso.identity_unlinked',
      'sso.user_provisioned',
      'identity.linked_by_admin',
      'impersonation.start',
      'impersonation.stop',
    ],
  },
  {
    group: 'Account',
    events: [
      'account.settings_updated',
      'account.security_updated',
      'account.capability_changed',
      'team.created',
      'team.updated',
      'team.deleted',
      'api_token.created',
      'api_token.shown',
      'api_token.regenerated',
      'api_token.revoked',
    ],
  },
  {
    group: 'Boards',
    events: [
      'create_board',
      'delete_board',
      'archive_board',
      'create_group',
      'delete_group',
      'create_column',
      'delete_column',
      'create_item',
      'delete_item',
      'update_column_value',
    ],
  },
];

/** A `<input type="date">` value is a local day; the reader wants an instant. */
const dayStart = (day: string): string => new Date(`${day}T00:00:00`).toISOString();
const dayEnd = (day: string): string => new Date(`${day}T23:59:59.999`).toISOString();

export function AuditScreen({ source }: { source: AdminSource }): React.ReactElement {
  const [sources, setSources] = useState<Array<'auth' | 'board'>>(['auth', 'board']);
  /* P-243 — §6.8's four filters, each one `auditInput` already accepted and nothing sent. */
  const [event, setEvent] = useState('');
  const [actor, setActor] = useState<PeopleSearchRow[]>([]);
  const [target, setTarget] = useState<PeopleSearchRow[]>([]);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  /** §6.8's `nextCursor`: the page used to show the newest 50 rows and stop, in silence. */
  const [pages, setPages] = useState(1);
  const toast = useOptionalToast();
  const [exporting, setExporting] = useState(false);

  const query = useMemo<AuditQuery>(
    () => ({
      sources,
      ...(event ? { events: [event] } : {}),
      ...(actor[0] ? { actorId: actor[0].id } : {}),
      ...(target[0] ? { targetId: target[0].id } : {}),
      ...(from ? { from: dayStart(from) } : {}),
      ...(to ? { to: dayEnd(to) } : {}),
      limit: 50 * pages,
    }),
    [sources, event, actor, target, from, to, pages],
  );

  const { data } = useAsync<{ rows: AuditRow[]; nextCursor?: string | null }>(
    () => source.audit(query),
    [JSON.stringify(query)],
  );

  const clearFilters = (): void => {
    setSources(['auth', 'board']);
    setEvent('');
    setActor([]);
    setTarget([]);
    setFrom('');
    setTo('');
    setPages(1);
  };

  /**
   * P-244 — **Export CSV**, with the filters that are on screen. The file is built by the api
   * (one redaction, one column order) and handed to the browser as a blob: the console never
   * re-serialises audit rows of its own.
   */
  const exportCsv = (): void => {
    setExporting(true);
    void source
      .exportAudit(query)
      .then(({ csv }) => {
        if (typeof document === 'undefined') return;
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        toast('Audit log exported', { kind: 'positive' });
      })
      .catch((cause: unknown) => {
        toast(cause instanceof Error ? cause.message : String(cause), { kind: 'negative' });
      })
      .finally(() => setExporting(false));
  };

  return (
    <div data-testid="admin-audit">
      <h1 className="ez-admin__heading">Audit log</h1>
      <p className="ez-admin__subheading">
        Account and sign-in events from the audit trail, merged with board activity. Retention is
        400 days.
      </p>
      <div className="ez-admin__toolbar">
        {(['auth', 'board'] as const).map((kind) => (
          <button
            key={kind}
            type="button"
            aria-pressed={sources.includes(kind)}
            onClick={() =>
              setSources((current) =>
                current.includes(kind) ? current.filter((c) => c !== kind) : [...current, kind],
              )
            }
          >
            {kind === 'auth' ? 'Account' : 'Boards'}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          data-testid="admin-audit-export"
          disabled={exporting}
          onClick={exportCsv}
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {/* §6.8's four filters (P-243). Every one of them was already accepted by `auditInput`. */}
      <div className="ez-admin-filter ez-admin-filter--audit" data-testid="admin-audit-filters">
        <div className="ez-admin-field">
          <label htmlFor="ez-audit-event">Event</label>
          <select
            id="ez-audit-event"
            value={event}
            onChange={(change) => {
              setEvent(change.target.value);
              setPages(1);
            }}
          >
            <option value="">All events</option>
            {AUDIT_EVENT_GROUPS.map((group) => (
              <optgroup key={group.group} label={group.group}>
                {group.events.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <PeoplePicker
          source={source}
          label="Actor"
          testId="admin-audit-actor"
          chosen={actor.slice(0, 1)}
          onChange={(next) => {
            setActor(next.slice(-1));
            setPages(1);
          }}
        />
        <PeoplePicker
          source={source}
          label="Target"
          testId="admin-audit-target"
          chosen={target.slice(0, 1)}
          onChange={(next) => {
            setTarget(next.slice(-1));
            setPages(1);
          }}
        />
        <div className="ez-admin-field">
          <label htmlFor="ez-audit-from">From</label>
          <input
            id="ez-audit-from"
            type="date"
            value={from}
            onChange={(change) => {
              setFrom(change.target.value);
              setPages(1);
            }}
          />
        </div>
        <div className="ez-admin-field">
          <label htmlFor="ez-audit-to">To</label>
          <input
            id="ez-audit-to"
            type="date"
            value={to}
            onChange={(change) => {
              setTo(change.target.value);
              setPages(1);
            }}
          />
        </div>
        <div className="ez-admin-filter__footer">
          <button type="button" onClick={clearFilters} data-testid="admin-audit-clear">
            Clear all
          </button>
          <span className="ez-admin__count">{data?.rows.length ?? 0} rows</span>
        </div>
      </div>

      <div className="ez-admin-table__scroll">
        <table className="ez-admin-table" data-testid="admin-audit-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Event</th>
              <th>Actor</th>
              <th>Target</th>
              <th>IP</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows ?? []).map((row) => (
              <tr key={row.id}>
                <td>{formatMondayDateTime(row.at)}</td>
                <td>
                  <span className="ez-admin-chip">{row.source}</span> {row.event}
                </td>
                <td>{row.actor?.name ?? ''}</td>
                <td>{row.target?.name ?? ''}</td>
                <td>{row.ip ?? ''}</td>
                <td title={JSON.stringify(row.data)}>{JSON.stringify(row.data).slice(0, 60)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data?.nextCursor ? (
        <button
          type="button"
          data-testid="admin-audit-more"
          onClick={() => setPages((current) => current + 1)}
        >
          Load more
        </button>
      ) : null}
    </div>
  );
}

const gb = (bytes: number): string => `${(bytes / 1024 ** 3).toFixed(2)}GB`;

export function UsageScreen({ source }: { source: AdminSource }): React.ReactElement {
  const { data } = useAsync<UsageStats>(() => source.usage(60), []);
  if (!data) return <div data-testid="admin-usage" />;
  return (
    <div data-testid="admin-usage">
      <h1 className="ez-admin__heading">Usage stats</h1>
      <h3>Summary</h3>
      <p className="ez-admin__subheading">Last {data.windowDays} days</p>
      <div className="ez-admin-stats">
        <Stat value={data.summary.boardsUpdated} label="Boards updated" />
        <Stat value={data.summary.peoplePosted} label="People posted" />
        <Stat value={data.summary.updates} label="Updates in boards" />
      </div>

      <h3>Storage used</h3>
      <p data-testid="admin-usage-storage">
        {gb(data.storage.usedBytes)} of{' '}
        {data.storage.quotaBytes === null ? 'unlimited' : gb(data.storage.quotaBytes)}
      </p>

      <h3>People activity</h3>
      <div className="ez-admin-stats">
        <Stat value={data.people.joined} label="People joined" />
        <Stat value={data.people.contributed} label="People contributed" />
        <Stat value={data.people.invitedNeverJoined} label="Invited but didn't join" />
      </div>

      <h3>Boards created</h3>
      <div className="ez-admin-stats">
        <Stat value={data.boardsCreated.main} label="Main boards" />
        <Stat value={data.boardsCreated.shared} label="Shared boards" />
        <Stat value={data.boardsCreated.private} label="Private boards" />
      </div>

      <h3>Seats</h3>
      <div className="ez-admin-stats">
        <Stat value={data.seats.admins} label="Admins" />
        <Stat value={data.seats.members} label="Members" />
        <Stat value={data.seats.guests} label="Guests" />
        <Stat value={data.seats.viewers} label="Viewers" />
        <Stat value={data.seats.pending} label="Pending" />
      </div>
    </div>
  );
}

function Stat({ value, label }: { value: number; label: string }): React.ReactElement {
  return (
    <div className="ez-admin-stat">
      <div className="ez-admin-stat__value">{value}</div>
      <div className="ez-admin-stat__label">{label}</div>
    </div>
  );
}
